import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import { Content, PaginationContainer, PaginationQuantity } from './styled';
import { Icon } from 'ui/styled';
import useI18n from 'hooks/useI18n';

// helpers to build a compact pagination range with ellipses
const range = (start, end) => Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);

function getPageItems(current, totalPages, { boundaryCount = 1, siblingCount = 1 } = {}) {
  // current and returned numbers are zero-based
  const startPages = range(0, Math.min(boundaryCount - 1, totalPages - 1));
  const endPages = range(Math.max(totalPages - boundaryCount, 0), totalPages - 1);
  const siblingsStart = Math.max(
    Math.min(current - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
    boundaryCount
  );
  const siblingsEnd = Math.min(
    Math.max(current + siblingCount, boundaryCount + siblingCount * 2),
    endPages.length > 0 ? endPages[0] - 1 : totalPages - 1
  );

  const items = [];
  items.push(...startPages);
  if (siblingsStart > boundaryCount) items.push('ellipsis');
  items.push(...range(siblingsStart, siblingsEnd));
  if (siblingsEnd < (endPages[0] ?? totalPages)) items.push('ellipsis');
  items.push(...endPages);

  // dedupe sequential ellipses
  return items.filter((v, i, a) => !(v === 'ellipsis' && a[i - 1] === 'ellipsis'));
}

const CustomPagination = styled(TablePagination)(({ theme }) => ({
  width: '100%',
  overflow: 'visible',
  display: 'flex',
  justifyContent: 'center',
  '& .MuiTablePagination-actions': {
    display: 'flex',
    marginLeft: 0,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  '& .MuiTablePagination-root': {
    width: '100%',
  },
  '& .MuiButtonBase-root': {
    margin: theme.spacing(0.5),
    minWidth: 36,
    width: 36,
    height: 38,
    backgroundColor: theme.palette.colors.backgroundgrey,
    color: theme.palette.colors.black,

    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    '&.Mui-selected': {
      color: theme.palette.colors.white,
      backgroundColor: theme.palette.blue.main,
    },
  },
  '& .MuiTablePagination-toolbar': {
    padding: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
    '& .MuiTablePagination-spacer': {
      display: 'none',
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-selectRoot': {
      display: 'none',
    },
  },
}));

export default function CustomTablePagination(props) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const { t } = useI18n()

  const pageCount = Math.max(1, Math.ceil((count || 0) / (rowsPerPage || 1)));
  const pageItems = getPageItems(page, pageCount, { boundaryCount: 1, siblingCount: 1 });

  const start = page * rowsPerPage + 1;
  const end = Math.min(count, (page + 1) * rowsPerPage);
  const showingText = `${t('showing')} ${count === 0 ? 0 : end - (start - 1)} ${t('of')} ${count} ${t('results')}`;

  return (
    <>
      <CustomPagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
        ActionsComponent={(subProps) => (
          <>

            <PaginationContainer>
              {/* <PaginationQuantity>
                {showingText}
              </PaginationQuantity> */}
              <Content className={subProps.className}>
                {/* Prev */}
                <Button
                  onClick={(event) => subProps.onPageChange(event, Math.max(0, subProps.page - 1))}
                  disabled={subProps.page === 0}
                >
                  <Icon icon="chevron-right" nomargin />
                </Button>

                {/* Page items with ellipses */}
                {pageItems.map((item, idx) => (
                  item === 'ellipsis' ? (
                    <Button key={`e-${idx}`} disabled>
                      …
                    </Button>
                  ) : (
                    <Button
                      key={item}
                      onClick={(event) => subProps.onPageChange(event, item)}
                      className={subProps.page === item ? 'Mui-selected' : ''}
                    >
                      {item + 1}
                    </Button>
                  )
                ))}

                <Button
                  onClick={(event) => subProps.onPageChange(event, Math.min(pageCount - 1, subProps.page + 1))}
                  disabled={subProps.page >= pageCount - 1}
                >
                  <Icon icon="chevron-left" nomargin />
                </Button>
              </Content>
            </PaginationContainer>
          </>
        )}
      />

    </>

  );
}
